import type { PenType, SheetType, Stroke, StrokesBlock } from '../types/models';
import { A4_LOGICAL_WIDTH, PAGE_MIN_HEIGHT } from './answers';

// Ink stroke rendering shared between the live InkCanvas (on-screen drawing) and the
// answer-sheet exporter (offscreen rasterisation for PDF). Keeping one implementation means a
// downloaded answer sheet looks exactly like what the student wrote on the tablet.

/** Per-segment line width for a nib. Pressure (avgP) varies more for fountain, none for ballpoint/marker. */
export function penLineWidth(pen: PenType | undefined, base: number, avgP: number): number {
  switch (pen) {
    case 'ballpoint': return base * 0.85;
    case 'marker': return base * 2.4;
    case 'fountain': return base * (0.25 + avgP * 1.7);
    case 'gel':
    default: return base * (0.6 + avgP * 0.9);
  }
}

/** Draw one stroke onto a 2D context already transformed into the page's logical coordinate space. */
export function drawStroke(ctx: CanvasRenderingContext2D, s: Stroke) {
  if (s.points.length < 2) return;
  ctx.save();
  ctx.strokeStyle = s.color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (let i = 1; i < s.points.length; i++) {
    const a = s.points[i - 1];
    const b = s.points[i];
    const avgP = (a.p + b.p) / 2;
    ctx.lineWidth = penLineWidth(s.pen, s.width, avgP);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
  ctx.restore();
}

// Paper colours for exported sheets. On screen these come from CSS custom properties; for a
// print/PDF we always want a light sheet with dark ink regardless of the app's theme.
const PAPER = '#ffffff';
const RULE = '#d7dde6';
const RULE_STRONG = '#9bb7d4';

/**
 * Paint the ruling/grid for a sheet onto a context transformed into logical space, mirroring the
 * CSS backgrounds in styles.css (`.ink-canvas-wrap.sheet-*`). `plain` draws nothing but paper.
 */
export function drawSheetBackground(
  ctx: CanvasRenderingContext2D, sheet: SheetType, width: number, height: number
) {
  ctx.save();
  const hline = (y: number, color: string, w = 1) => {
    ctx.strokeStyle = color; ctx.lineWidth = w;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
  };
  const vline = (x: number, color: string, w = 1) => {
    ctx.strokeStyle = color; ctx.lineWidth = w;
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
  };
  switch (sheet) {
    case 'ruled':
      for (let y = 32; y < height; y += 32) hline(y, RULE);
      break;
    case 'graph':
      for (let x = 0; x <= width; x += 24) vline(x, RULE);
      for (let y = 0; y <= height; y += 24) hline(y, RULE);
      break;
    case 'engineering':
      for (let x = 0; x <= width; x += 8) vline(x, RULE, 0.5);
      for (let y = 0; y <= height; y += 8) hline(y, RULE, 0.5);
      for (let x = 0; x <= width; x += 40) vline(x, RULE_STRONG);
      for (let y = 0; y <= height; y += 40) hline(y, RULE_STRONG);
      break;
    case 'log':
      for (let x = 0; x <= width; x += 24) vline(x, RULE);
      for (let y = 0; y < height; y += 12) hline(y, RULE);
      for (let y = 0; y < height; y += 60) hline(y, RULE_STRONG);
      break;
    case 'plain':
    default:
      break;
  }
  ctx.restore();
}

/** The sheet type of a page, tolerating legacy pages that only carry the `ruled` flag. */
export function sheetTypeOf(block: StrokesBlock): SheetType {
  return (block.sheetType ?? (block.ruled === false ? 'plain' : 'ruled')) as SheetType;
}

/** The single colour staff correct with (a firm red), used on-screen and in the exported PDF. */
export const ANNOTATION_COLOR = '#dc2626';

// Ink pages start at a full A4 so students have room, but most answers fill only the top. We crop
// to the content (+ a little padding) when reviewing/printing so blank tails don't waste space or
// PDF pages. Values are logical px in the A4 coordinate space.
const CROP_PAD = 40;
const CROP_MIN = 96;

/** The largest y coordinate touched by any of the given stroke lists (0 if none). */
export function strokesMaxY(...lists: (Stroke[] | undefined)[]): number {
  let maxY = 0;
  for (const list of lists)
    for (const s of list ?? [])
      for (const p of s.points) if (p.y > maxY) maxY = p.y;
  return maxY;
}

/**
 * The height a page should render/display at, cropped to its content plus padding. Returns 0 when
 * the page (and its annotation) is completely blank, so callers can skip it entirely.
 */
export function croppedLogicalHeight(
  block: StrokesBlock, annotation?: Stroke[], pad = CROP_PAD, min = CROP_MIN
): number {
  const full = Math.max(PAGE_MIN_HEIGHT, block.height || PAGE_MIN_HEIGHT);
  const maxY = strokesMaxY(block.strokes, annotation);
  if (maxY <= 0) return 0;
  return Math.min(full, Math.max(min, Math.ceil(maxY + pad)));
}

/**
 * Rasterise one ink page (paper background + student strokes + optional red-pen annotation) to a
 * PNG data URL for embedding in a PDF, cropped to its content. `scale` is the backing-store
 * multiplier over the logical A4 space (2 keeps handwriting crisp). Returns '' for a blank page.
 */
export function renderPageToPngDataUrl(block: StrokesBlock, scale = 2, annotation?: Stroke[]): string {
  const width = block.width || A4_LOGICAL_WIDTH;
  const height = croppedLogicalHeight(block, annotation);
  if (height <= 0) return '';
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  drawSheetBackground(ctx, sheetTypeOf(block), width, height);
  for (const s of block.strokes) drawStroke(ctx, s);
  for (const s of annotation ?? []) drawStroke(ctx, s);
  return canvas.toDataURL('image/png');
}
